/**
 * Enterprise & bulk contact form. Self-serve creators just buy credits; this is
 * the "Contact us" CTA for teams, volume, brand control, and API. Posts to
 * /api/billing/upgrade-request which writes to the Waitlist table with the full
 * form payload in formData (and optionally pings Slack).
 *
 * Used by:
 *   - /pricing page: the CTA on the "Enterprise & bulk" tier.
 *   - account billing: the "Need more?" → enterprise contact.
 *   - UpgradeModal: the "buying for a team / in bulk?" link under the picker.
 */

import { useState } from 'react';
import { Loader2, ArrowRight, CheckCircle2 } from 'lucide-react';
import api from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';

type VolumeBucket = '1-5' | '5-20' | '20-100' | '100+';
type TeamBucket = 'solo' | '2-10' | '10-50' | '50+';
// Sent under the `pricingPreferences` payload key (kept for backend compat); the
// values describe what the team needs, not a price point.
type NeedType = 'bulk_credits' | 'team_seats' | 'brand_kit' | 'api_access' | 'white_label' | 'not_sure';

const VOLUME_OPTIONS: { value: VolumeBucket; label: string }[] = [
  { value: '1-5', label: '1–5' },
  { value: '5-20', label: '5–20' },
  { value: '20-100', label: '20–100' },
  { value: '100+', label: '100+' },
];

const TEAM_OPTIONS: { value: TeamBucket; label: string }[] = [
  { value: 'solo', label: 'Just me' },
  { value: '2-10', label: '2–10' },
  { value: '10-50', label: '10–50' },
  { value: '50+', label: '50+' },
];

const NEEDS_OPTIONS: { value: NeedType; label: string; subtitle: string }[] = [
  { value: 'bulk_credits', label: 'Bulk credits', subtitle: 'Volume pricing for lots of videos' },
  { value: 'team_seats', label: 'Team seats', subtitle: 'Shared workspace for multiple people' },
  { value: 'brand_kit', label: 'Brand kit & templates', subtitle: 'Your fonts, colors, and custom templates' },
  { value: 'api_access', label: 'API access', subtitle: 'Generate videos programmatically' },
  { value: 'white_label', label: 'Managed / white-label', subtitle: 'Run it under your own brand or deployment' },
  { value: 'not_sure', label: 'Not sure yet', subtitle: 'Want to talk it through' },
];

interface Props {
  source: string; // e.g. 'pricing_page', 'account_billing', 'upgrade_modal'
  defaultEmail?: string;
  defaultName?: string;
  onSuccess?: () => void;
}

export function UpgradeRequestForm({ source, defaultEmail, defaultName, onSuccess }: Props) {
  const [name, setName] = useState(defaultName ?? '');
  const [email, setEmail] = useState(defaultEmail ?? '');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [linkedinOrWebsite, setLinkedin] = useState('');

  const [useCase, setUseCase] = useState('');
  const [videosPerMonth, setVideosPerMonth] = useState<VolumeBucket | ''>('');
  const [teamSize, setTeamSize] = useState<TeamBucket | ''>('');

  const [needs, setNeeds] = useState<NeedType[]>([]);
  const [notes, setNotes] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  function toggle<T extends string>(arr: T[], value: T): T[] {
    return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
  }

  const canSubmit =
    !busy &&
    name.trim().length > 0 &&
    email.trim().length > 0 &&
    useCase.trim().length >= 5 &&
    videosPerMonth !== '' &&
    teamSize !== '' &&
    needs.length > 0;

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.post('/billing/upgrade-request', {
        name: name.trim(),
        email: email.trim(),
        company: company.trim() || undefined,
        role: role.trim() || undefined,
        linkedinOrWebsite: linkedinOrWebsite.trim() || undefined,
        useCase: useCase.trim(),
        videosPerMonth,
        teamSize,
        // Backend still keys this `pricingPreferences`; values are now needs.
        pricingPreferences: needs,
        betaFitWhy: notes.trim() || undefined,
        source,
      });
      setDone(true);
      onSuccess?.();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: unknown } }; message?: string };
      const raw = e?.response?.data?.error;
      const msg =
        typeof raw === 'string'
          ? raw
          : raw && typeof raw === 'object'
          ? ((raw as { message?: string }).message || JSON.stringify(raw))
          : e?.message || 'Could not submit. Try again.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="flex flex-col items-center text-center gap-3 py-6">
        <CheckCircle2 className="h-10 w-10 text-primary" />
        <div className="text-lg font-semibold">Got it — we'll be in touch</div>
        <p className="text-sm text-muted-foreground max-w-sm">
          Thanks for reaching out. We'll get back to you about volume pricing and team setup within a couple of business days.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Name" required>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Jane Doe" disabled={busy} />
        </Field>
        <Field label="Work email" required>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@company.com" disabled={busy} />
        </Field>
        <Field label="Company">
          <Input value={company} onChange={(e) => setCompany(e.target.value)} disabled={busy} />
        </Field>
        <Field label="Role">
          <Input value={role} onChange={(e) => setRole(e.target.value)} placeholder="Founder, marketing lead, …" disabled={busy} />
        </Field>
      </div>

      <Field label="LinkedIn or website">
        <Input value={linkedinOrWebsite} onChange={(e) => setLinkedin(e.target.value)} placeholder="linkedin.com/in/…" disabled={busy} />
      </Field>

      <Field label="What are you making videos for?" required help="A sentence is plenty (at least a few words).">
        <Textarea
          value={useCase}
          onChange={(e) => setUseCase(e.target.value)}
          placeholder="Course modules, marketing demos, internal training, …"
          rows={3}
          disabled={busy}
        />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Videos per month" required>
          <RadioGroup options={VOLUME_OPTIONS} value={videosPerMonth} onChange={(v) => setVideosPerMonth(v as VolumeBucket)} disabled={busy} />
        </Field>
        <Field label="Team size" required>
          <RadioGroup options={TEAM_OPTIONS} value={teamSize} onChange={(v) => setTeamSize(v as TeamBucket)} disabled={busy} />
        </Field>
      </div>

      <Field label="What do you need?" required help="Pick all that apply.">
        <div className="space-y-2">
          {NEEDS_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer hover:bg-muted/50"
            >
              <Checkbox
                checked={needs.includes(opt.value)}
                onCheckedChange={() => setNeeds(toggle(needs, opt.value))}
                disabled={busy}
                className="mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">{opt.label}</div>
                {opt.subtitle && <div className="text-xs text-muted-foreground">{opt.subtitle}</div>}
              </div>
            </label>
          ))}
        </div>
      </Field>

      <Field label="Anything else we should know?" help="Optional.">
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Timeline, budget, who else is involved, what you're trying to ship"
          disabled={busy}
        />
      </Field>

      {error && <div className="text-sm text-destructive">{error}</div>}

      {!canSubmit && !busy && (
        <p className="text-xs text-muted-foreground text-center">
          {!name.trim() || !email.trim()
            ? 'Add your name and work email to continue.'
            : useCase.trim().length < 5
            ? 'Tell us a bit more about what you’re making videos for.'
            : videosPerMonth === '' || teamSize === ''
            ? 'Pick your videos-per-month and team size.'
            : needs.length === 0
            ? 'Pick at least one thing you need.'
            : ''}
        </p>
      )}

      <Button onClick={submit} disabled={!canSubmit} className="w-full">
        {busy ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
        ) : (
          <>Send inquiry <ArrowRight className="w-4 h-4 ml-1" /></>
        )}
      </Button>
    </div>
  );
}

function Field({
  label,
  required,
  help,
  children,
}: {
  label: string;
  required?: boolean;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-sm font-medium">
        {label}
        {required && <span className="text-destructive ml-0.5">*</span>}
      </Label>
      {children}
      {help && <p className="text-xs text-muted-foreground">{help}</p>}
    </div>
  );
}

function RadioGroup<T extends string>({
  options,
  value,
  onChange,
  disabled,
}: {
  options: { value: T; label: string }[];
  value: string;
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            disabled={disabled}
            className={
              'px-3 py-1.5 text-sm rounded-md border transition ' +
              (active
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-background hover:bg-muted border-border')
            }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
